import { FormattedMessage } from 'react-intl';

import type { SecondaryCTAProps } from '@/components/ProductDetail/components';
import type {
  OrderAppCtaType,
  OrderAppProductVariantBusinessDetailsFragment,
} from '@/graphql/orderApp/api';
import type { Maybe } from '@/lib/types';
import { commonMessages, GiveAsAGift } from '@/messages/common';

export interface SecondaryNotifyMeOpts {
  businessData: Maybe<OrderAppProductVariantBusinessDetailsFragment>;
  distributionBCode?: string | null;
  isNotifyMe?: boolean;
  productCatalogMode?: boolean;
  variantCTA?: OrderAppCtaType;
}

/**
 * CASE A: Sellable WEB = YES, Stock = NO, Distribution status = B2
 * CASE B: Sellable WEB = YES, Stock = NO, Distribution status = B4
 * Additional business rules: isNotifyMe master switch must be enabled, not in
 * catalog mode.
 *
 * Lifted out of the component unchanged so this chain is budgeted here rather
 * than against an already long render (sonar typescript:S3776).
 */
export const isSecondaryNotifyMeVisible = ({
  businessData,
  distributionBCode,
  isNotifyMe,
  productCatalogMode,
  variantCTA,
}: SecondaryNotifyMeOpts): boolean =>
  Boolean(
    isNotifyMe &&
    variantCTA !== 'NOTIFY' &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (businessData as any)?.webSellable === true &&
    (businessData?.quantityAvailable === 0 ||
      !businessData?.quantityAvailable) &&
    (distributionBCode === 'B2' || distributionBCode === 'B4') &&
    !productCatalogMode,
  );

export interface SecondaryCtaOpts {
  is4GiftsAvailable: boolean;
  isAvailabilityRequestAvailable?: boolean;
  isCallToBuyAvailable: boolean;
  isCallToPreOrderAvailable: boolean;
  isSecondaryNotifyMeAvailable: boolean;
  onAvailabilityRequest: () => void;
  onBookAppointment: () => void;
  onCallToBuy: () => void;
  onGiveAsAGift: () => void;
  onNotifyMe: () => void;
  productCatalogMode?: boolean;
  variantCTA?: OrderAppCtaType;
}

/**
 * Assembles the secondary CTA list. Lifted out of the component so the flag
 * checks are budgeted against this function rather than adding to an already
 * long render (sonar typescript:S3776). Order is unchanged: call to pre-order,
 * call to buy and 4Gift lead, then book an appointment, notify me and
 * availability request.
 */
export const buildSecondaryCtaItems = ({
  is4GiftsAvailable,
  isAvailabilityRequestAvailable,
  isCallToBuyAvailable,
  isCallToPreOrderAvailable,
  isSecondaryNotifyMeAvailable,
  onAvailabilityRequest,
  onBookAppointment,
  onCallToBuy,
  onGiveAsAGift,
  onNotifyMe,
  productCatalogMode,
  variantCTA,
}: SecondaryCtaOpts): SecondaryCTAProps['items'] => {
  const items: SecondaryCTAProps['items'] = [
    {
      icon: 'calendar',
      id: 'bookAppointment',
      message: <FormattedMessage {...commonMessages.bookAppointment} />,
      onClick: onBookAppointment,
    },
  ];

  if (isSecondaryNotifyMeAvailable) {
    items.push({
      icon: 'mail',
      id: 'notifyMe',
      message: <FormattedMessage {...commonMessages.notifyMe} />,
      onClick: onNotifyMe,
    });
  }

  // BEC-24926: the two swap places on mobile only. 4Gift appears here instead
  // of its button, and Call to buy moves up to the button - so each entry is
  // limited to the breakpoint where it belongs. No icon on 4Gift: the
  // GiveAsAGift message already renders the gift glyph inline.
  if (is4GiftsAvailable && variantCTA === 'ORDER')
    items.unshift({
      hiddenOn: 'desktop',
      id: 'giveAsAGift',
      message: GiveAsAGift,
      onClick: onGiveAsAGift,
    });

  if (isCallToBuyAvailable && !productCatalogMode)
    items.unshift({
      hiddenOn: 'mobile',
      icon: 'telephoneCall',
      id: 'callToBuy',
      message: <FormattedMessage {...commonMessages.callToBuy} />,
      onClick: onCallToBuy,
    });

  if (isCallToPreOrderAvailable && !productCatalogMode)
    items.unshift({
      icon: 'telephoneCall',
      id: 'callToPreOrder',
      message: <FormattedMessage {...commonMessages.callToPreOrder} />,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClick: () => {},
    });

  if (isAvailabilityRequestAvailable) {
    items.push({
      icon: 'mail',
      id: 'availabilityRequest',
      message: <FormattedMessage {...commonMessages.availabilityRequest} />,
      onClick: onAvailabilityRequest,
    });
  }

  return items;
};
